import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import api from '../../../utils/api';
import { toast } from 'sonner';

const AddProductModal = ({ isOpen, onClose, onAddProduct, initialValues }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sku: '',
    category: '',
    supplier: '',
    price: '',
    cost: '',
    stock: '',
    reorderPoint: '',
    barcode: '',
    weight: '',
    dimensions: '',
    isActive: true,
    trackInventory: true,
    imageUrl: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({ ...prev, ...(initialValues || {}) }));
    }
  }, [isOpen, initialValues]);

  // Debounced lookup when barcode changes
  useEffect(() => {
    if (!formData.barcode) return;
    const code = String(formData.barcode).trim();
    if (code.length < 8) return; // wait until plausible barcode length
    const t = setTimeout(async () => {
      await handleLookupBarcode(code);
    }, 500);
    return () => clearTimeout(t);
  }, [formData.barcode]);

  // Category is now a text input field with comma-separated values

  // Supplier is now a text input field

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const applyLookupToForm = (p) => {
    if (!p) return;
    setFormData(prev => ({
      ...prev,
      name: p.name || prev.name,
      description: p.description || prev.description,
      sku: p.sku || prev.sku,
      category: p.category || prev.category,
      supplier: p.supplier || prev.supplier,
      price: p.price != null ? String(p.price) : prev.price,
      cost: p.cost != null ? String(p.cost) : prev.cost,
      stock: p.quantity != null ? String(p.quantity) : prev.stock,
      reorderPoint: p.reorderPoint != null ? String(p.reorderPoint) : prev.reorderPoint,
      weight: p.weight != null ? String(p.weight) : prev.weight,
      dimensions: p.dimensions || prev.dimensions,
      isActive: p.isActive != null ? p.isActive : prev.isActive,
      trackInventory: p.trackInventory != null ? p.trackInventory : prev.trackInventory,
      imageUrl: p.imageUrl || prev.imageUrl, // Ensure imageUrl is included
    }));
  };

  const handleLookupBarcode = async (code) => {
    if (!code) return;
    setIsLookingUp(true);
    try {
      const { data } = await api.get(`/barcode/${encodeURIComponent(code)}`);
      console.log('Barcode lookup response:', data);
      
      if (data) {
        // Create a copy of data to process
        let processedData = { ...data };
        
        // Process category data consistently regardless of source
        if (data.source === 'firestore' && data.id) {
          // Product found in our inventory
          if (Array.isArray(data.category)) {
            // Take only the first 3 categories
            processedData.category = data.category.slice(0, 3).join(', ');
          } else if (typeof data.category === 'string') {
            processedData.category = data.category;
          } else {
            processedData.category = '';
          }
        } else if (data.source === 'openfoodfacts') {
          // Map Open Food Facts data to our form format
          let categoryValue = '';
          
          // Handle category as array or string
          if (Array.isArray(data.category)) {
            // Take only the first 3 categories
            categoryValue = data.category.slice(0, 3).join(', ');
          } else if (data.category) {
            categoryValue = data.category;
          }
          
          // If allCategories is available, use that instead
          if (data.allCategories && Array.isArray(data.allCategories) && data.allCategories.length > 0) {
            // Take only the first 3 categories
            categoryValue = data.allCategories.slice(0, 3).join(', ');
          }
          
          processedData = {
            name: data.productName || '',
            description: data.description || '',
            sku: data.sku || '',
            category: categoryValue,
            supplier: data.supplier || '',
            price: data.sellingPrice != null ? String(data.sellingPrice) : '',
            cost: data.costPrice != null ? String(data.costPrice) : '',
            stock: data.initialStock != null ? String(data.initialStock) : '',
            reorderPoint: data.reorderPoint != null ? String(data.reorderPoint) : '',
            barcode: data.barcode || code,
            weight: data.weight || '',
            dimensions: data.dimensions || '',
            isActive: data.isActive != null ? data.isActive : true,
            trackInventory: data.trackInventory != null ? data.trackInventory : true,
            imageUrl: data.imageUrl 
          };
        }
        
        // Ensure we have the image URL
        if (data.imageUrl && !processedData.imageUrl) {
          processedData.imageUrl = data.imageUrl;
        }
        
        // Debug log to verify data
        console.log('Processed data for form:', processedData);
        
        // Apply the processed data to the form
        applyLookupToForm(processedData);
      }
    } catch (e) {
      console.error('Barcode lookup error:', e);
      toast.error('Failed to lookup barcode');
      // Not found is fine; user can continue manual entry
    } finally {
      setIsLookingUp(false);
    }
  };

  const generateSKU = () => {
    const prefix = formData?.category ? formData?.category?.substring(0, 3)?.toUpperCase() : 'PRD';
    const timestamp = Date.now()?.toString()?.slice(-6);
    const randomNum = Math.floor(Math.random() * 100)?.toString()?.padStart(2, '0');
    return `${prefix}-${timestamp}-${randomNum}`;
  };

  const handleGenerateSKU = () => {
    const newSKU = generateSKU();
    handleInputChange('sku', newSKU);
  };

  const validateForm = async () => {
    const newErrors = {};
    if (!formData?.name?.trim()) newErrors.name = 'Product name is required';
    if (!formData?.sku?.trim()) newErrors.sku = 'SKU is required';
    if (!formData?.category?.trim()) newErrors.category = 'At least one category is required';
    if (!formData?.price || parseFloat(formData?.price) <= 0) newErrors.price = 'Valid price is required';
    if (!formData?.stock || parseInt(formData?.stock) < 0) newErrors.stock = 'Valid stock quantity is required';
    if (!formData?.reorderPoint || parseInt(formData?.reorderPoint) < 0) newErrors.reorderPoint = 'Valid reorder point is required';
    
    // Check barcode uniqueness if barcode is provided
    if (formData?.barcode?.trim()) {
      try {
        const { data } = await api.get(`/barcode/${formData.barcode}`);
        // If product exists and it's not the one we're editing
        if (data?.id && data?.id !== formData?.id) {
          newErrors.barcode = 'This barcode is already in use by another product';
        }
      } catch (error) {
        // No product found with this barcode, which is good for a new product
        console.log('Barcode is unique');
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/file', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const imageUrl = data?.url;
      console.log('Image upload response:', data);
      console.log('Image URL received:', imageUrl);
      
      if (imageUrl) {
        // Directly set the imageUrl in formData
        setFormData(prev => {
          const updated = { ...prev, imageUrl };
          console.log('Updated formData with imageUrl:', updated);
          return updated;
        });
        toast.success('Image uploaded successfully');
      } else {
        console.error('No image URL received from server');
        toast.error('Failed to get image URL from server');
      }
    } catch (e) {
      console.error('Upload failed', e);
      toast.error('Failed to upload image');
    } finally { 
      setUploading(false); 
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const isValid = await validateForm();
    if (!isValid) return;
    setIsSubmitting(true);
    try {
      // Process categories as an array from comma-separated values
      const categoryArray = formData.category
        ? formData.category.split(',').map(cat => cat.trim()).filter(cat => cat)
        : [];
      
      // Create a new object without spreading formData to avoid any issues
      // IMPORTANT FIX: Ensure stock value is properly parsed as a number with a default of 0
      // Parse the stock value carefully to avoid NaN issues
      let stockValue = 0;
      if (formData.stock !== undefined && formData.stock !== null && formData.stock !== '') {
        stockValue = Number(formData.stock);
        if (isNaN(stockValue)) stockValue = 0;
      }
      
      console.log('Stock value before submission:', stockValue, typeof stockValue);
      
      const productData = {
        name: formData.name,
        description: formData.description,
        sku: formData.sku,
        supplier: formData.supplier,
        barcode: formData.barcode,
        dimensions: formData.dimensions,
        isActive: formData.isActive,
        trackInventory: formData.trackInventory,
        category: categoryArray, // Convert to array for database storage
        price: parseFloat(formData.price),
        cost: formData.cost ? parseFloat(formData.cost) : 0,
        stock: stockValue, // Keep stock for backward compatibility
        quantity: stockValue, // Explicitly set quantity for database
        reorderPoint: parseInt(formData.reorderPoint),
        weight: formData.weight ? parseFloat(formData.weight) : null,
        imageUrl: formData.imageUrl ? formData.imageUrl : null, // Explicitly set to null if not present
        id: formData.id || Date.now(), // Use existing ID if available (for editing)
        lastUpdated: new Date().toISOString()
      };
      
      console.log('Final product data being sent:', productData);
      await onAddProduct(productData);
      handleClose();
    } catch (error) {
      console.error('Error adding product:', error);
      toast.error('Failed to add product');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      sku: '',
      category: '',
      supplier: '',
      price: '',
      cost: '',
      stock: '',
      reorderPoint: '',
      barcode: '',
      weight: '',
      dimensions: '',
      isActive: true,
      trackInventory: true,
      imageUrl: ''
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
      <div className="bg-card border border-border rounded-lg shadow-modal w-full max-w-2xl mx-2 sm:mx-4 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-semibold text-foreground">Add New Product</h2>
            <Button variant="ghost" size="icon" onClick={handleClose} disabled={isSubmitting} className="h-10 w-10 sm:h-8 sm:w-8">
              <Icon name="X" size={20} />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            {/* Basic Information */}
            <div className="space-y-3 sm:space-y-4">
              <h3 className="text-base sm:text-lg font-medium text-foreground border-b border-border pb-2">Basic Information</h3>
              <div className="mb-4">
                <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                  {formData?.imageUrl ? (
                    <img src={formData?.imageUrl} alt="Product" className="w-20 h-20 sm:w-24 sm:h-24 object-cover border rounded" />
                  ) : (
                    <div className="w-20 h-20 sm:w-24 sm:h-24 bg-muted flex items-center justify-center border rounded">
                      <Icon name="Image" size={20} className="text-muted-foreground sm:hidden" />
                      <Icon name="Image" size={24} className="text-muted-foreground hidden sm:block" />
                    </div>
                  )}
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-foreground mb-1">Product Image</label>
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleUpload} 
                      className="text-sm text-foreground"
                      disabled={uploading}
                    />
                    {uploading && <div className="text-sm text-muted-foreground mt-1">Uploading...</div>}
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <Input label="Product Name" type="text" placeholder="Enter product name" value={formData?.name} onChange={(e) => handleInputChange('name', e?.target?.value)} error={errors?.name} required />
                <div className="flex space-x-2">
                  <Input label="SKU" type="text" placeholder="Product SKU" value={formData?.sku} onChange={(e) => handleInputChange('sku', e?.target?.value)} error={errors?.sku} required className="flex-1" />
                  <Button type="button" variant="outline" onClick={handleGenerateSKU} className="mt-6 h-10 w-10 sm:h-auto sm:w-auto" title="Generate SKU">
                    <Icon name="RefreshCw" size={16} />
                  </Button>
                </div>
              </div>
              <Input label="Description" type="text" placeholder="Product description (optional)" value={formData?.description} onChange={(e) => handleInputChange('description', e?.target?.value)} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <Input label="Categories" type="text" placeholder="Enter categories separated by commas" value={formData?.category} onChange={(e) => handleInputChange('category', e?.target?.value)} error={errors?.category} description="Separate multiple categories with commas" required />
                <Input label="Supplier" type="text" placeholder="Enter supplier name" value={formData?.supplier} onChange={(e) => handleInputChange('supplier', e?.target?.value)} />
              </div>
            </div>

            {/* Pricing & Inventory */}
            <div className="space-y-3 sm:space-y-4">
              <h3 className="text-base sm:text-lg font-medium text-foreground border-b border-border pb-2">Pricing & Inventory</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <Input label="Selling Price" type="number" step="0.01" placeholder="0.00" value={formData?.price} onChange={(e) => handleInputChange('price', e?.target?.value)} error={errors?.price} required />
                <Input label="Cost Price" type="number" step="0.01" placeholder="0.00" value={formData?.cost} onChange={(e) => handleInputChange('cost', e?.target?.value)} description="Optional - for profit margin calculations" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <Input label="Initial Stock" type="number" placeholder="0" value={formData?.stock} onChange={(e) => handleInputChange('stock', e?.target?.value)} error={errors?.stock} required />
                <Input label="Reorder Point" type="number" placeholder="0" value={formData?.reorderPoint} onChange={(e) => handleInputChange('reorderPoint', e?.target?.value)} error={errors?.reorderPoint} description="Alert when stock falls below this level" required />
              </div>
            </div>

            {/* Additional Details */}
            <div className="space-y-3 sm:space-y-4">
              <h3 className="text-base sm:text-lg font-medium text-foreground border-b border-border pb-2">Additional Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <div className="flex space-x-2">
                  <Input label="Barcode" type="text" placeholder="Product barcode" value={formData?.barcode} onChange={(e) => handleInputChange('barcode', e?.target?.value)} />
                  <Button type="button" variant="outline" className="mt-6 h-10 w-10 sm:h-auto sm:w-auto" onClick={() => handleLookupBarcode(String(formData.barcode || '').trim())} loading={isLookingUp} disabled={isLookingUp || !formData.barcode} title="Lookup by barcode">
                    <Icon name="Search" size={16} />
                  </Button>
                </div>
                <Input label="Weight (kg)" type="number" step="0.01" placeholder="0.00" value={formData?.weight} onChange={(e) => handleInputChange('weight', e?.target?.value)} />
              </div>
              <Input label="Dimensions" type="text" placeholder="L x W x H (optional)" value={formData?.dimensions} onChange={(e) => handleInputChange('dimensions', e?.target?.value)} />
              <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
                <Checkbox label="Active Product" checked={formData?.isActive} onChange={(e) => handleInputChange('isActive', e?.target?.checked)} description="Product is available for sale" />
                <Checkbox label="Track Inventory" checked={formData?.trackInventory} onChange={(e) => handleInputChange('trackInventory', e?.target?.checked)} description="Monitor stock levels for this product" />
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 sm:pt-6 border-t border-border">
              <Button type="button" variant="ghost" onClick={handleClose} disabled={isSubmitting} className="w-full sm:w-auto">Cancel</Button>
              <Button type="submit" variant="default" loading={isSubmitting} iconName="Plus" iconPosition="left" iconSize={16} className="w-full sm:w-auto">
                Add Product
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddProductModal;